import React, { useState } from 'react';
import { Button, Typography, Alert, Modal, Image, Spin } from 'antd';
import { 
  SendOutlined
} from '@ant-design/icons';
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { DamageLocations } from '../../../redux/reducers/damageLocationsSlice';
import { 
  PhotoTypes,
  DisplayPhoto,
  areAllRequiredPhotosReady,
} from '../../../redux/reducers/vehiclePhotosSlice';
import Api from '../../../services/api';
import Insights from '../../../services/insightsService';
import { UpdateIncidentRequest } from '../../../types/api.types';
import { setIsFinalized } from '../../../redux/reducers/incidentDetailsSlice';
import { resetIncident } from '../../../redux/store';
import { transformIncidentData } from '@/core/helpers/transform';
import { photoStorage } from '../../../services/photoStorage';
import { useNavigate } from 'react-router-dom';
import AuthService from '../../../services/auth';
import { setCurrentStep } from '../../../redux/reducers/navigationSlice';

const { Title, Text } = Typography;

const sectionStyle: React.CSSProperties = { padding: 16 };
const sectionLabel: React.CSSProperties = { 
  textTransform: 'uppercase', 
  fontWeight: 600, 
  fontSize: 13, 
  color: '#595959',
  paddingTop: 10, 
  paddingBottom: 10 
};
const rowStyle: React.CSSProperties = { 
  display: 'flex', 
  justifyContent: 'space-between', 
  alignItems: 'center', 
  padding: '16px 0', 
  borderBottom: '1px solid #f0f0f0' 
};
const labelStyle: React.CSSProperties = { fontSize: 15, color: '#8c8c8c', paddingLeft: 16 };

interface ReviewSubmitStepProps {
  onNext: () => void;
  onPrevious: () => void;
  onComplete: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

const ReviewSubmitStep: React.FC<ReviewSubmitStepProps> = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const userRoles = useAppSelector((state) => state.auth.userData?.roles ?? []);
  const isOperator = AuthService.isOperator(userRoles);

  const vehicle = useAppSelector((state) => state.vehicle);
  const incidentDetails = useAppSelector((state) => state.incidentDetails);
  const damageLocations = useAppSelector((state) => state.damageLocations);
  const damagedParts = useAppSelector((state) => state.damagedParts);
  const vehiclePhotos = useAppSelector((state) => state.vehiclePhotos);
  const operatorInfo = useAppSelector((state) => state.operatorInfo);
  const auth = useAppSelector((state) => state.auth);

  const buildUpdateRequest = (): UpdateIncidentRequest => {
    return transformIncidentData({
      damageLocations,
      damagedParts,
      incidentDetails,
      vehicle,
      vehiclePhotos,
      operatorInfo,
      auth
    }, true) as UpdateIncidentRequest;
  };

  const handleSubmit = async () => {
    if (!areAllRequiredPhotosReady(vehiclePhotos.photoMap)) {
      setError('Required photos are missing or failed to upload. Go back to Vehicle Photos and retake them.');
      return;
    }

    setSubmitting(true);
    setError(null);
    
    try {
      const incidentData = buildUpdateRequest();
      incidentData.Finalize = true;
      
      // Update the existing shell incident (created at VehicleConfirmationStep)
      // The Update endpoint is the only path that saves photos as IncidentFiles
      await Api.updateIncident(incidentData);
      
      dispatch(setIsFinalized(true));
      
      Modal.success({
        title: 'Success',
        content: 'Damage summary added!',
        onOk: async () => {
          dispatch(resetIncident());
          await photoStorage.clearAll();
          navigate('/home');
        },
      });
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : 'Failed to submit incident. Please try again.');
      Insights.trackException(error instanceof Error ? error : new Error(
        typeof error === "object" && error !== null
          ? JSON.stringify(error)
          : String(error)
      ));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="step-container">
      <Title level={5} style={{ textAlign: 'center' }}>Damage Summary</Title>

      {error && (
        <Alert
          message="Submission Error"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginTop: 20, marginBottom: 20 }}
        />
      )}

      <div style={{ marginTop: 20 }}>
        {/* Vehicle Summary */}
        <div style={sectionStyle}>
          <Text style={sectionLabel}>Vehicle Summary</Text>
          <div style={rowStyle}>
            <Text style={labelStyle}>Year</Text>
            <Text>{vehicle.vehicle?.year || ''}</Text>
          </div>
          <div style={rowStyle}>
            <Text style={labelStyle}>Make</Text>
            <Text>{vehicle.vehicle?.make || ''}</Text>
          </div>
          <div style={rowStyle}>
            <Text style={labelStyle}>Model</Text>
            <Text>{vehicle.vehicle?.model || ''}</Text>
          </div>
          <div style={rowStyle}>
            <Text style={labelStyle}>Color</Text>
            <Text>{vehicle.vehicle?.color || ''}</Text>
          </div>
        </div>

        {/* Damage Photos */}
        <div style={sectionStyle}>
          <Text style={sectionLabel}>Damage Photos</Text>
          <div style={{ display: 'flex', flexDirection: 'row', overflowX: 'auto', marginTop: 10, gap: 10 }}>
            {Object.entries(vehiclePhotos.photoMap).map(([type, photo]) => {
              if (!photo) return null;
              const src = (photo as DisplayPhoto).uri || (photo as DisplayPhoto).url;
              if (!src) return null;
              return (
                <Image
                  key={type}
                  src={src}
                  alt={PhotoTypes[parseInt(type)]}
                  style={{ height: 60, width: 60, objectFit: 'cover', flexShrink: 0 }}
                />
              );
            })}
          </div>
        </div>

        {/* Damage Location */}
        <div style={sectionStyle}>
          <Text style={sectionLabel}>Damage Location</Text>
          <div style={rowStyle}>
            <Text>
              {(Object.entries(damageLocations.damageLocation) as [DamageLocations, boolean][])
                .filter(([, checked]) => checked)
                .map(([name]) => name)
                .join(', ') || 'None'}
            </Text>
          </div>
        </div>

        {/* Damaged Parts */}
        <div style={sectionStyle}>
          <Text style={sectionLabel}>Damaged Parts</Text>
          <div style={rowStyle}>
            <Text>
              {[
                ...Object.entries(damagedParts.frontDamagedParts).filter(([, v]) => v).map(([k]) => k),
                ...Object.entries(damagedParts.sideDamagedParts).filter(([, v]) => v).map(([k]) => k),
                ...Object.entries(damagedParts.rearDamagedParts).filter(([, v]) => v).map(([k]) => k),
              ].join(', ') || 'None'}
            </Text>
          </div>
        </div>

        {/* Vehicle Status */}
        <div style={sectionStyle}>
          <Text style={sectionLabel}>Vehicle Status</Text>
          <div style={rowStyle}>
            <Text style={labelStyle}>Drivable?</Text>
            <Text>{vehicle.vehicleIsDrivable ? 'Yes' : 'No'}</Text>
          </div>
          <div style={rowStyle}>
            <Text style={labelStyle}>Mileage</Text>
            <Text>{vehicle.currentMileage?.toString() || ''}</Text>
          </div>
          {vehicle.additionalInformation && (
            <div style={rowStyle}>
              <Text>{vehicle.additionalInformation}</Text>
            </div>
          )}
        </div>
      </div>

      <div className="step-actions" style={{ marginTop: 40 }}>
        {isOperator && (
          <Button
            size="large"
            onClick={() => dispatch(setCurrentStep(7))}
            disabled={submitting}
            style={{ color: '#FF9902', fontWeight: 'bold' }}
          >
            ADD INCIDENT DETAILS
          </Button>
        )}
        <Button 
          type="primary" 
          icon={submitting ? <Spin /> : <SendOutlined />}
          onClick={handleSubmit}
          loading={submitting}
          size="large"
          style={{ fontWeight: 'bold' }}
        >
          {submitting ? 'SUBMITTING...' : 'SUBMIT INCIDENT'}
        </Button>
      </div>
    </div>
  );
};

export default ReviewSubmitStep;